import React, { useState } from 'react';
import {
  Box, Grid, Typography, TextField, Button, Divider, MenuItem, Paper, Snackbar, Alert as MuiAlert
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

const culturas = ['Soja', 'Milho', 'Café', 'Cana', 'Algodão', 'Outra'];
const topografia = ['Plano', 'Levemente Ondulado', 'Montanhoso'];
const clima = ['Seco', 'Úmido', 'Tropical', 'Semiárido'];
const irrigacao = ['Natural', 'Irrigado', 'Gotejamento', 'Pivot'];

const FarmForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    nome: '',
    culturaPrincipal: '',
    outrasCulturas: '',
    hectaresTotal: '',
    hectaresPlantados: '',
    tipoSolo: '',
    responsavelTecnico: '',
    dataInicioSafra: '',
    endereco: '',
    imagem: null,
    location: { lat: -15.7801, lng: -47.9292 },
    topografia: '',
    clima: '',
    irrigacao: '',
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    setFormData({ ...formData, imagem: e.target.files[0] });
  };

  const handleGeocode = async () => {
    if (!formData.endereco) return;
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.endereco)}`);
      const data = await response.json();
      if (data.length > 0) {
        const { lat, lon } = data[0];
        setFormData({ ...formData, location: { lat: parseFloat(lat), lng: parseFloat(lon) } });
      } else {
        alert('Endereço não encontrado.');
      }
    } catch (error) {
      alert('Erro ao buscar localização.');
      console.error(error);
    }
  };

  const MapClick = () => {
    useMapEvents({
      click(e) {
        setFormData({ ...formData, location: e.latlng });
      }
    });
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();

    Object.keys(formData).forEach((key) => {
      if (key === 'location') {
        formDataToSend.append('latitude', formData.location.lat);
        formDataToSend.append('longitude', formData.location.lng);
      } else {
        formDataToSend.append(key, formData[key]);
      }
    });

    try {
      const response = await axios.post(
        'https://sense-agro-backend.onrender.com/api/farm',
        formDataToSend,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      console.log('Fazenda salva:', response.data);
      setSnackbarOpen(true);

      setFormData({
        nome: '',
        culturaPrincipal: '',
        outrasCulturas: '',
        hectaresTotal: '',
        hectaresPlantados: '',
        tipoSolo: '',
        responsavelTecnico: '',
        dataInicioSafra: '',
        endereco: '',
        imagem: null,
        location: { lat: -15.7801, lng: -47.9292 },
        topografia: '',
        clima: '',
        irrigacao: '',
      });

      if (onSave) onSave();
    } catch (error) {
      console.error('Erro ao salvar fazenda:', error);
      alert('Erro ao salvar a fazenda. Confira os campos obrigatórios.');
    }
  };

  return (
    <Box sx={{ padding: 4, backgroundColor: '#fff', borderRadius: '8px', boxShadow: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '30px', color: '#333' }}>
        Cadastro de Fazenda
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Campos */}
          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="nome" label="Nome da Fazenda" fullWidth value={formData.nome} onChange={handleChange} />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField select name="culturaPrincipal" label="Cultura Principal" fullWidth value={formData.culturaPrincipal} onChange={handleChange}>
                {culturas.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="outrasCulturas" label="Outras Culturas" fullWidth value={formData.outrasCulturas} onChange={handleChange} />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="hectaresTotal" label="Hectares Total" type="number" fullWidth value={formData.hectaresTotal} onChange={handleChange} />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="hectaresPlantados" label="Hectares Plantados" type="number" fullWidth value={formData.hectaresPlantados} onChange={handleChange} />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="responsavelTecnico" label="Responsável Técnico" fullWidth value={formData.responsavelTecnico} onChange={handleChange} />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="tipoSolo" label="Tipo de Solo" fullWidth value={formData.tipoSolo} onChange={handleChange} />
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="dataInicioSafra" label="Data Início Safra" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.dataInicioSafra} onChange={handleChange} />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField name="endereco" label="Endereço" fullWidth value={formData.endereco} onChange={handleChange} />
              <Button variant="contained" onClick={handleGeocode} sx={{ marginTop: 1 }}>
                Buscar Localização
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField select name="topografia" label="Topografia" fullWidth value={formData.topografia} onChange={handleChange}>
                {topografia.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField select name="clima" label="Clima" fullWidth value={formData.clima} onChange={handleChange}>
                {clima.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <TextField select name="irrigacao" label="Irrigação" fullWidth value={formData.irrigacao} onChange={handleChange}>
                {irrigacao.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <input type="file" accept="image/*" onChange={handleImageChange} />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ padding: 2, borderRadius: '8px' }}>
              <MapContainer center={[formData.location.lat, formData.location.lng]} zoom={5} style={{ height: '300px', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapClick />
                <Marker position={[formData.location.lat, formData.location.lng]} />
              </MapContainer>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" type="submit" fullWidth>Salvar Fazenda</Button>
          <Button variant="outlined" onClick={onCancel} fullWidth>Cancelar</Button>
        </Box>
      </form>

      {/* Popup */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Fazenda salva com sucesso!
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default FarmForm;
